// @ts-nocheck
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Calendar, FolderOpen, Code, MessageSquare, Briefcase, BarChart3,
  CreditCard, ListChecks, Palette, Plus, X, ExternalLink, RefreshCw, CheckCircle2,
  AlertTriangle, Search, ArrowRight, Loader2, Trash2,
} from 'lucide-react';
import {
  PROVIDER_CATEGORIES,
  resolveProvider,
  type CategoryInfo,
  type ProviderInfo,
} from './providerCatalog';
import { revokeConnection } from '../../services/onboardingApi';
import { BRAND } from '../../constants/brand';
import { useTileFlash } from './useTileFlash';
import { openAppConnectHub } from './appConnect/appConnectEvents';
import { getNextStepsFor, type NextStepChip } from './connectionNextSteps';
import { useNavigate } from 'react-router-dom';

/**
 * Connected Apps section for the My Business / playbook page.
 *
 * Three things this surface needs to do well:
 *
 *   1. Show what's plugged in, organized — group by category, branded
 *      logos, status at a glance. The previous version was a flat list of
 *      monogram chips; this groups by Communication / Files / Code /
 *      etc. so a customer with 8 connections doesn't see a wall.
 *
 *   2. Let the customer manage a single connection — click a chip, see a
 *      small popover with status, last sync, error, and a re-authorize
 *      button when the token has expired. (Disconnect requires a separate
 *      revoke endpoint we haven't built yet — Pass 3.)
 *
 *   3. Let the customer add new connections without leaving the page —
 *      "Connect more" button opens a slim picker with the full catalog.
 *      The actual OAuth uses the same /onboarding/initiate-auth endpoint
 *      the Connect lane uses. Synchronous popup, status polling, the lot.
 *
 * Connection lifecycle UI:
 *   idle           → green dot, "Connected" label
 *   expired        → yellow dot, "Re-auth needed" label, [Re-authorize] action
 *   pending        → amber dot, "Authorizing…" label
 *   error          → red dot, "Failed" label, error tooltip
 */

const ICON_MAP: Record<string, React.ElementType> = {
  mail: Mail,
  calendar: Calendar,
  folder: FolderOpen,
  code: Code,
  chat: MessageSquare,
  crm: Briefcase,
  analytics: BarChart3,
  payments: CreditCard,
  tasks: ListChecks,
  design: Palette,
};

export interface ConnectionRow {
  id: string;
  provider: string;
  status: string;          // 'connected' | 'pending' | 'expired' | 'failed' | etc.
  last_sync_at?: string | null;
  error?: string | null;
}

interface ConnectedAppsSectionProps {
  connections: ConnectionRow[];
  /** aibot client id — passed to initiateOnboardingAuth for OAuth setup. */
  clientId?: string | null;
  /** Optional: callback when a new connection completes, so the parent can refresh. */
  onConnected?: (provider: string) => void;
}

function ProviderLogo({ provider, size = 24 }: { provider: ProviderInfo; size?: number }) {
  return (
    <div
      className="flex items-center justify-center rounded-lg font-bold shrink-0"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.46,
        background: provider.color,
        color: provider.textColor || '#FFFFFF',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 1px 2px rgba(0,0,0,0.10)',
        letterSpacing: '-0.5px',
      }}
      aria-hidden
    >
      {provider.initial}
    </div>
  );
}

function statusForRow(row: ConnectionRow): { kind: 'ok' | 'warn' | 'pending' | 'error'; label: string; dotColor: string } {
  const s = String(row.status || '').toLowerCase();
  if (s === 'connected' || s === 'active') return { kind: 'ok', label: 'Connected', dotColor: BRAND.green };
  if (s === 'expired')                      return { kind: 'warn', label: 'Re-auth needed', dotColor: BRAND.amber };
  if (s === 'pending' || s === 'authorizing') return { kind: 'pending', label: 'Authorizing…', dotColor: BRAND.amber };
  if (s === 'failed' || s === 'error')      return { kind: 'error', label: 'Failed', dotColor: BRAND.red };
  return { kind: 'ok', label: row.status || 'Unknown', dotColor: '#9CA3AF' };
}

function formatRelative(iso?: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const ms = Date.now() - t;
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

const ConnectedAppsSection: React.FC<ConnectedAppsSectionProps> = ({ connections, clientId, onConnected }) => {
  const navigate = useNavigate();
  const [openManageId, setOpenManageId] = useState<string | null>(null);
  // Disconnect lifecycle: 'confirm' shows the destructive-action confirmation
  // inside the popover; 'pending' shows a spinner while the revoke is in flight.
  const [disconnectState, setDisconnectState] = useState<{ id: string; phase: 'confirm' | 'pending' } | null>(null);

  // Fresh-connect banner: when a new provider becomes connected (diffed
  // against the previous render), surface a short success message.
  const [freshConnect, setFreshConnect] = useState<{ slug: string; provider: ProviderInfo } | null>(null);
  const prevConnectedSlugsRef = useRef<Set<string> | null>(null);

  // Section-level flash hook — yellow ring when a new provider becomes
  // connected. Also auto-listens for `orbit:tile-update {section:'connections'}`.
  const { flashStyle } = useTileFlash('connections');

  // Group connections by category. Synthesized providers (unknown slug) land
  // in an "Other" bucket so they still render with sensible defaults.
  const grouped = useMemo(() => {
    const map = new Map<string, { category: CategoryInfo | { id: string; label: string; iconKey: string }; rows: Array<{ row: ConnectionRow; provider: ProviderInfo }> }>();
    for (const row of connections || []) {
      const { provider, category } = resolveProvider(row.provider);
      const cat = category || { id: 'other', label: 'Other', iconKey: 'tasks' };
      if (!map.has(cat.id)) map.set(cat.id, { category: cat as any, rows: [] });
      map.get(cat.id)!.rows.push({ row, provider });
    }
    // Order: follow PROVIDER_CATEGORIES, then any extras (Other) last.
    const ordered: Array<{ category: any; rows: Array<{ row: ConnectionRow; provider: ProviderInfo }> }> = [];
    for (const cat of PROVIDER_CATEGORIES) if (map.has(cat.id)) ordered.push(map.get(cat.id)!);
    for (const [id, entry] of map) if (!PROVIDER_CATEGORIES.find(c => c.id === id)) ordered.push(entry);
    return ordered;
  }, [connections]);

  const connectedSlugs = useMemo(
    () => new Set(
      (connections || [])
        .filter(c => {
          const s = String(c.status || '').toLowerCase();
          return s === 'connected' || s === 'active';
        })
        .map(c => c.provider),
    ),
    [connections],
  );

  // Diff connectedSlugs across renders
  // a short success banner and trigger the tile flash.
  useEffect(() => {
    const prev = prevConnectedSlugsRef.current;
    if (prev) {
      for (const slug of connectedSlugs) {
        if (!prev.has(slug)) {
          const { provider } = resolveProvider(slug);
          try {
            window.dispatchEvent(new CustomEvent('orbit:tile-update', { detail: { section: 'connections', slug } }));
          } catch { /* noop */ }
          setFreshConnect({ slug, provider });
          window.setTimeout(() => {
            setFreshConnect(curr => (curr && curr.slug === slug ? null : curr));
          }, 90_000);
          break;
        }
      }
    }
    prevConnectedSlugsRef.current = new Set(connectedSlugs);
  }, [connectedSlugs]);

  const handleReauth = useCallback((provider: string) => {
    setOpenManageId(null);
    openAppConnectHub({ provider, tab: 'add' });
  }, []);

  const handleDisconnect = useCallback(async (rowId: string, providerSlug: string) => {
    setDisconnectState({ id: rowId, phase: 'pending' });
    try {
      await revokeConnection({ provider: providerSlug, clientId: clientId || undefined });
      // Close the popover; parent re-fetches → chip disappears
      setOpenManageId(null);
      onConnected?.(providerSlug);
    } catch (err: any) {
      console.warn('[ConnectedApps] revoke failed:', err?.message || err);
      // Stay open; flip back to confirm so the user can see the error and retry/cancel
      setDisconnectState({ id: rowId, phase: 'confirm' });
    } finally {
      setDisconnectState(null);
    }
  }, [clientId, onConnected]);

  // Close the manage popover on Escape.
  useEffect(() => {
    if (!openManageId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenManageId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openManageId]);

  const startNextStep = useCallback((provider: ProviderInfo, step: NextStepChip) => {
    const type = step.route === 'one_off' ? 'app_workflow' : step.route;
    if (type !== 'workspace_build' && type !== 'marketplace') {
      navigate(`/capabilities/new?type=${encodeURIComponent(type)}&provider=${encodeURIComponent(provider.label)}`);
      return;
    }
    const text = `${step.prompt}\n\nConnected app: ${provider.label}.`;
    window.dispatchEvent(new CustomEvent('orbit:open-with-prompt', {
      detail: {
        text,
        route: 'problem',
        context: 'problem',
        fresh: true,
        source: 'connected_app_next_step',
        provider: provider.slug,
        workRoute: step.route,
      },
    }));
  }, [navigate]);

  return (
    <section className="bg-white rounded-card border border-line overflow-hidden" style={flashStyle}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-line-soft">
        <div>
          <h3 className="text-[14px] font-semibold text-ink">Connected Apps</h3>
          <p className="text-[11.5px] text-ink-dim mt-0.5">
            Tools Orbit can read from. {connections?.length || 0} connected.
          </p>
        </div>
        <button
          type="button"
          onClick={() => openAppConnectHub({ tab: 'add' })}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold transition-colors whitespace-nowrap"
          style={{
            background: BRAND.blue,
            color: 'white',
            boxShadow: '0 2px 6px rgba(24,25,29,0.22)',
          }}
        >
          <Plus className="w-3.5 h-3.5" />
          Connect more
        </button>
      </div>

      {/* Fresh-connect success banner */}
      <AnimatePresence>
        {freshConnect && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
            className="px-5 py-3 border-b border-line-soft"
            style={{
              background: `linear-gradient(135deg, rgba(24,25,29,0.06) 0%, rgba(255,234,0,0.05) 100%)`,
            }}
          >
            <div className="flex items-center gap-3">
              <ProviderLogo provider={freshConnect.provider} size={28} />
              <div className="flex-1 min-w-0">
                <span className="text-[12.5px] font-semibold text-ink">
                  {freshConnect.provider.label} connected and ready.
                </span>
              </div>
              <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
              <button
                type="button"
                onClick={() => setFreshConnect(null)}
                className="p-1 text-ink-soft hover:text-ink shrink-0"
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Body — grouped by category */}
      {grouped.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-panel bg-canvas mb-3">
            <Plus className="w-5 h-5 text-ink-soft" />
          </div>
          <div className="text-[13px] text-ink font-medium mb-1">Nothing connected yet</div>
          <div className="text-[12px] text-ink-dim mb-4">Connect a tool so Orbit can read your business data and act on it.</div>
          <button
            type="button"
            onClick={() => openAppConnectHub({ tab: 'add' })}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12.5px] font-semibold whitespace-nowrap"
            style={{ background: BRAND.blue, color: 'white' }}
          >
            <Plus className="w-3.5 h-3.5" />
            Connect your first app
          </button>
        </div>
      ) : (
        <div className="divide-y divide-line-soft">
          {grouped.map(({ category, rows }) => {
            const Icon = ICON_MAP[(category as any).iconKey] || ListChecks;
            return (
              <div key={(category as any).id} className="px-5 py-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-3.5 h-3.5 text-ink-soft" />
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-dim">
                    {(category as any).label}
                  </span>
                  <span className="text-[10.5px] text-ink-soft">· {rows.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {rows.map(({ row, provider }) => {
                    const status = statusForRow(row);
                    const isOpen = openManageId === row.id;
                    const nextSteps = getNextStepsFor(provider.slug).slice(0, 3);
                    return (
                      <div key={row.id} className="relative">
                        <div
                          className="rounded-card border bg-white transition-all duration-150 hover:bg-canvas"
                          style={{
                            borderColor: isOpen ? 'rgba(24,25,29,0.35)' : 'rgba(17,24,39,0.08)',
                            boxShadow: isOpen ? '0 4px 14px rgba(24,25,29,0.10)' : 'none',
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => setOpenManageId(isOpen ? null : row.id)}
                            className="w-full flex items-center gap-3 p-2.5 text-left"
                          >
                            <ProviderLogo provider={provider} size={28} />
                            <div className="flex-1 min-w-0">
                              <div className="text-[13px] font-semibold text-ink truncate">
                                {provider.label}
                              </div>
                              <div className="text-[11px] text-ink-dim mt-0.5 flex items-center gap-1.5">
                                <span
                                  className="w-1.5 h-1.5 rounded-full shrink-0"
                                  style={{ background: status.dotColor }}
                                />
                                {status.label}
                                {row.last_sync_at && status.kind === 'ok' && (
                                  <>
                                    <span className="text-ink-soft">·</span>
                                    <span>{formatRelative(row.last_sync_at)}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </button>
                          <div className="border-t border-line-soft px-2.5 py-2">
                            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-soft">
                              Next steps
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {nextSteps.map((step) => (
                                <button
                                  key={step.label}
                                  type="button"
                                  onClick={() => startNextStep(provider, step)}
                                  className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-semibold text-brand-700 ring-1 ring-brand-100 transition-colors hover:bg-brand-100 whitespace-nowrap"
                                  title={step.prompt}
                                >
                                  <span>{step.label}</span>
                                  <span className="rounded-full bg-white/70 px-1.5 py-0.5 text-[9px] font-semibold text-ink-dim ring-1 ring-brand-100">
                                    {step.badge}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Manage popover */}
                        <AnimatePresence>
                          {isOpen && (
                            <>
                              {/* Click-outside catcher */}
                              <div
                                className="fixed inset-0 z-40"
                                onClick={() => setOpenManageId(null)}
                                aria-hidden
                              />
                              <motion.div
                                initial={{ opacity: 0, y: 4, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 2 }}
                                transition={{ duration: 0.15 }}
                                className="absolute z-50 left-0 right-0 top-full mt-1 bg-white rounded-card border border-line p-4 space-y-3"
                                style={{ boxShadow: '0 12px 36px rgba(0,0,0,0.10)' }}
                              >
                                <div className="flex items-start gap-3">
                                  <ProviderLogo provider={provider} size={32} />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-[14px] font-semibold text-ink">{provider.label}</div>
                                    <div className="text-[11.5px] text-ink-dim mt-0.5">
                                      Connection ID: <span className="font-sans text-ink">{row.id.slice(0, 8)}…</span>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setOpenManageId(null)}
                                    className="p-1 text-ink-soft hover:text-ink"
                                    aria-label="Close"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>

                                {/* Status + last sync */}
                                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-line-soft">
                                  <div>
                                    <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-soft">Status</div>
                                    <div className="text-[12.5px] mt-0.5 flex items-center gap-1.5" style={{ color: BRAND.black }}>
                                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.dotColor }} />
                                      {status.label}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-soft">Last sync</div>
                                    <div className="text-[12.5px] mt-0.5 text-ink">{formatRelative(row.last_sync_at)}</div>
                                  </div>
                                </div>

                                {row.error && (
                                  <div className="text-[11.5px] p-2 rounded-md flex items-start gap-1.5" style={{ background: 'rgba(220,38,38,0.06)', color: '#991B1B' }}>
                                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                                    <span>{row.error}</span>
                                  </div>
                                )}

                                {/* Actions row */}
                                {disconnectState?.id === row.id ? (
                                  // Confirm-disconnect view replaces the normal action row.
                                  // Two-step pattern (click Disconnect → confirm) is the
                                  // standard guard for destructive actions.
                                  <div className="space-y-2">
                                    <div className="text-[11.5px] text-ink">
                                      Disconnect <span className="font-semibold">{provider.label}</span>?
                                      Orbit will lose access to this app's data. You can reconnect anytime.
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => handleDisconnect(row.id, provider.slug)}
                                        disabled={disconnectState.phase === 'pending'}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors whitespace-nowrap"
                                        style={{
                                          background: '#DC2626', color: 'white',
                                          opacity: disconnectState.phase === 'pending' ? 0.6 : 1,
                                        }}
                                      >
                                        {disconnectState.phase === 'pending' ? (
                                          <><Loader2 className="w-3 h-3 animate-spin" />Disconnecting…</>
                                        ) : (
                                          <><Trash2 className="w-3 h-3" />Yes, disconnect</>
                                        )}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setDisconnectState(null)}
                                        disabled={disconnectState.phase === 'pending'}
                                        className="px-3 py-1.5 rounded-md text-[12px] text-ink-muted hover:bg-canvas"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    {(status.kind === 'warn' || status.kind === 'error') && (
                                      <button
                                        type="button"
                                        onClick={() => handleReauth(provider.slug)}
                                        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-semibold transition-all whitespace-nowrap"
                                        style={{ background: BRAND.blue, color: 'white' }}
                                      >
                                        <RefreshCw className="w-3.5 h-3.5" />
                                        Re-authorize
                                      </button>
                                    )}
                                    {status.kind === 'ok' && (
                                      <div className="text-[11.5px] text-ink-dim italic">
                                        Working as expected. Nothing for you to do.
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => setDisconnectState({ id: row.id, phase: 'confirm' })}
                                      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 mt-2 rounded-md text-[11.5px] font-medium text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                      Disconnect
                                    </button>
                                  </>
                                )}
                              </motion.div>
                            </>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

    </section>
  );
};

export default ConnectedAppsSection;
